import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useQueryParameters from '@afosto/utils-react/useQueryParameters';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ServiceCostForm from '../../components/ServiceCostForm';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import translations from './translations';
import { services as servicesApi } from '@afosto/order/api/Services';

const CreateServiceCost = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'odr:orders:read',
    'odr:orders:write',
  ]);

  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useQueryParameters();
  const [isFetchingServiceCost, setIsFetchingServiceCost] = useState(true);
  const [serviceCost, setServiceCost] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchServiceCost = async serviceId => {
      try {
        const response = await servicesApi.getService(serviceId);
        const { data = {} } = response;

        if (isMounted) {
          setServiceCost({ ...data });
        }
      } catch (error) {
        if (isMounted) {
          setServiceCost(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingServiceCost(false);
        }
      }
    };

    if (id) {
      fetchServiceCost(id);
    } else {
      setIsFetchingServiceCost(false);
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleCreateServiceCost = async ({
    name,
    amount,
    isPercentage,
    constraints,
    filters,
    priority,
    vatSettings,
    type,
    activeAt,
    expiresAt,
  }) => {
    try {
      await servicesApi.createService({
        name,
        amount,
        is_percentage: isPercentage,
        constraints,
        filters,
        priority,
        type,
        active_at: activeAt,
        expires_at: expiresAt,
        vat_settings: vatSettings,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.serviceCostCreated), {
        variant: 'success',
      });

      history.push('/service-costs');
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.serviceCostNotCreated), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingServiceCost) {
    return <SkeletonPage fullWidth />;
  }

  if (id && !serviceCost) {
    return <h1>service not found!</h1>;
  }

  return (
    <Page title={intl.formatMessage(translations.title)}>
      <ServiceCostForm
        onSubmit={handleCreateServiceCost}
        onCancel={() => history.push('/service-costs')}
        service={serviceCost}
      />
    </Page>
  );
};

export default CreateServiceCost;
